import React, { type JSX } from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { type CategoryInfo } from '../models/CategoryInfo';

export type CategoryContext = {
  close: () => void;
};

type CategoryPickerProps = {
  categories: CategoryInfo[];
  activeCategoryId: string;
  onRenderText?: (context: CategoryContext) => JSX.Element;
  onRenderItems?: (context: CategoryContext) => JSX.Element;
  onCategoryChanged: (category: CategoryInfo) => void;
};

@observer
export default class CategoryPicker extends React.Component<CategoryPickerProps> {
  @observable private _isOpen = false;
  private _containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: CategoryPickerProps){
    super(props);
    makeObservable(this);
  }

  async componentDidMount(): Promise<void>{
    document.addEventListener('click', this._onDocumentClick);
  }

  componentWillUnmount(){
    document.removeEventListener('click', this._onDocumentClick);
  }

  render(): JSX.Element{
    const { categories, activeCategoryId, onRenderText, onRenderItems } = this.props;

    let itemsClass = 'catpick-menu';
    if (!this._isOpen){
      itemsClass += ' hide';
    }

    const context: CategoryContext = {
      close: () => {
        this._isOpen = false;
      }
    };

    const activeCategory = categories.find(c => c.id === activeCategoryId);
    const activeColor = activeCategory?.color;
    return (
      <div className="catpick" ref={this._containerRef}>
        <button
          type="button"
          className="catpick-btn"
          aria-haspopup="true"
          aria-expanded={this._isOpen}
          aria-label="Change category"
          onClick={this._onClick}
        >
          <span
            className={`catpick-dot${activeColor ? '' : ' uncat'}`}
            style={activeColor ? { background: activeColor } : undefined}
          />
          <span className="catpick-name">{activeCategory?.name || '[No name]'}</span>
          <svg className="catpick-chev" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
          {onRenderText?.(context)}
        </button>
        <div className={itemsClass}>
          <div className="catpick-items">
            {categories.map(c => {
              const active = c.id === activeCategoryId;
              return (
                <div
                  key={c.id}
                  className={`catpick-item${active ? ' active' : ''}`}
                  onClick={() => this._onCategorySelected(c)}
                >
                  <span className={`catpick-dot${c.color ? '' : ' uncat'}`} style={c.color ? { background: c.color } : undefined} />
                  <span className="catpick-name">{c.name || '[No name]'}</span>
                  <svg className="catpick-check" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                </div>
              );
            })}
            {onRenderItems?.(context)}
          </div>
        </div>
      </div>
    );
  }

  @action.bound
  private _onClick(){
    this._isOpen = !this._isOpen;
  }

  @action.bound
  private _onDocumentClick(e: MouseEvent){
    if (e.target && this._containerRef.current?.contains(e.target as Node)){
      return;
    }

    this._isOpen = false;
  }

  @action.bound
  private _onCategorySelected(c: CategoryInfo){
    this.props.onCategoryChanged(c);
    this._isOpen = false;
  }
}